import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, Upload, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRatingChange, interactive = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-5 h-5 ${
            interactive ? 'cursor-pointer' : ''
          } ${
            star <= (hover || rating)
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-gray-700'
          }`}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          onClick={() => interactive && onRatingChange(star)}
        />
      ))}
    </div>
  );
};

const SESSION_OPTIONS = [
  'Solo Studio',
  'Group Session',
  'Birthday Party',
  'Team Building',
  '30 Minute Session',
  '1 Hour Session'
];

export const Testimonials = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    comment: '',
    rating: 0,
    photo: '',
    sessionAttended: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);
  
  // Prefill from review-request email link (?name=...&session=...)
  useEffect(() => {
    const prefillName = searchParams.get('name');
    const prefillSession = searchParams.get('session');
    if (prefillName || prefillSession) {
      setFormData(prev => ({
        ...prev,
        name: prefillName || prev.name,
        sessionAttended: prefillSession || prev.sessionAttended
      }));
      setTimeout(() => {
        document.getElementById('testimonial-form')?.scrollIntoView({ behavior: 'smooth' });
      }, 400);
    }
  }, [searchParams]);
  
  // Fetch testimonials from API
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/testimonials/approved`);
        if (response.data.success) {
          setTestimonials(response.data.data);
        }
      } catch (error) {
        toast.error('Failed to load testimonials');
      } finally {
        setLoadingTestimonials(false);
      }
    };
    
    fetchTestimonials();
  }, [API_URL]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    if (!formData.sessionAttended) {
      toast.error('Please select a session');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/testimonials`, formData);
      
      if (response.data.success) {
        toast.success('Thank you for your testimonial!', {
          description: 'Your review has been posted immediately!'
        });
        
        // Refresh testimonials to show the new one
        const testimonialsResponse = await axios.get(`${API_URL}/api/testimonials/approved`);
        if (testimonialsResponse.data.success) {
          setTestimonials(testimonialsResponse.data.data);
        }
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          comment: '',
          rating: 0,
          photo: '',
          sessionAttended: ''
        });
      } else {
        toast.error('Failed to submit testimonial', {
          description: response.data.message || 'Please try again later.'
        });
      }
    } catch (error) {
      toast.error('Failed to submit testimonial', {
        description: 'Please try again later or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-white">
      {/* Header */}
      <section className="px-6 py-12">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-pink-600 to-purple-600 mb-4">What Our Guests Say</h1>
          <p className="text-xl text-gray-700">
            Real experiences from our creative community
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="px-6 py-12">
        <div className="container mx-auto">
          {loadingTestimonials ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading testimonials...</p>
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No testimonials yet. Be the first to share your experience!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="bg-white border-2 border-purple-200 p-6 rounded-2xl shadow-lg hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4 mb-4">
                    {testimonial.photo && (
                      <img 
                        src={testimonial.photo} 
                        alt={testimonial.name}
                        className="w-16 h-16 rounded-full object-cover ring-2 ring-yellow-400"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{testimonial.name}</h3>
                      <StarRating rating={testimonial.rating} />
                      <p className="text-xs text-gray-500 mt-1">{new Date(testimonial.date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-4">
                    "{testimonial.comment}"
                  </p>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-pink-400 font-semibold">
                      {testimonial.sessionAttended}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Submission Form */}
      <section className="px-6 py-12">
        <div className="container mx-auto max-w-3xl">
          <div id="testimonial-form" className="bg-white border-2 border-purple-200 p-8 rounded-2xl shadow-2xl">
            <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-pink-500 text-center">Share Your Experience</h2>
            <p className="text-gray-700 text-center mb-8">
              Had a great time? We'd love to hear about it!
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Session Attended *
                </label>
                <Select
                  value={formData.sessionAttended}
                  onValueChange={(value) => value && setFormData((prev) => ({ ...prev, sessionAttended: value }))}
                  required
                >
                  <SelectTrigger className="w-full" data-testid="testimonial-session-select">
                    <SelectValue placeholder="Select a session">
                      {formData.sessionAttended || undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {SESSION_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                    {formData.sessionAttended && !SESSION_OPTIONS.includes(formData.sessionAttended) && formData.sessionAttended !== 'Other' && (
                      <SelectItem value={formData.sessionAttended}>{formData.sessionAttended}</SelectItem>
                    )}
                    <SelectItem value="Other">Other Session</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Rating *
                </label>
                <StarRating 
                  rating={formData.rating} 
                  onRatingChange={(rating) => setFormData({ ...formData, rating })}
                  interactive={true}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Photo URL (Optional)
                </label>
                <div className="flex gap-2">
                  <Input
                    type="url"
                    name="photo"
                    value={formData.photo}
                    onChange={handleChange}
                    placeholder="https://example.com/your-photo.jpg"
                  />
                  <Button type="button" variant="outline" className="flex-shrink-0">
                    <Upload className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Add a profile photo or leave blank for default</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Testimonial *
                </label>
                <Textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleChange}
                  placeholder="Tell us about your experience..."
                  required
                  rows={5}
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-pink-400 to-pink-500 hover:from-pink-500 hover:to-pink-600 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Testimonial'}
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
